import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useClassificationList } from '@/api/queries/catalog-queries'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import { Button } from '@/components/ui/button'
import { Field, FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import { useInteracaoForm } from '@/hooks/forms/use-interacao-form'
import type { InteracaoFormData } from '@/schemas/interacao-schema'
import type { Potential as PotentialValue } from '@/types'
import type {
	Interacao,
	InteracaoAction,
	InteracaoUpdateInput,
} from '@/types/interacao'
import type { OrgCR } from '@/types/organization'
import { formatDateYMD, parseStringToDateForDisplay } from '@/utils/formatDate'
import {
	INSPECTION_COLORS,
	InspectionDateField,
	InspectionOptionGroup,
} from '../inspections'
import { Spinner } from '../ui/spinner'

interface PersonEdit {
	id: string
	name: string
	badge: string
}

export interface CorrectInteracaoFormProps {
	interacaoSelected?: Interacao
	persons: PersonEdit[]
	crList?: OrgCR[]
	isLoading?: boolean
	onSave: (data: InteracaoUpdateInput) => void
	onClose: () => void
}

export function CorrectInteracaoFormContent({
	interacaoSelected,
	persons,
	crList,
	isLoading = false,
	onSave,
	onClose,
}: CorrectInteracaoFormProps) {
	const { t } = useTranslation()
	const form = useInteracaoForm()
	const {
		setFocus,
		formState: { errors },
	} = form
	const { handleError } = useFormErrorFocus(errors, setFocus)
	const { data: classificationData } = useClassificationList()

	const colors = INSPECTION_COLORS.violet
	const classificationId = form.watch('classificationId')
	const [selectedCrId, setSelectedCrId] = useState<string>(
		interacaoSelected?.crId ?? '',
	)

	useEffect(() => {
		const parsedDate = parseStringToDateForDisplay(
			interacaoSelected?.interactionDate ?? '',
		)
		const initialPotential = (
			interacaoSelected?.potential ?? 'n5'
		).toLowerCase() as PotentialValue

		form.reset({
			date: parsedDate ? formatDateYMD(parsedDate) : '',
			employeeId: interacaoSelected?.employeeId ?? '',
			leaderId: interacaoSelected?.leaderId ?? '',
			potential: initialPotential,
			classificationId: interacaoSelected?.classificationId ?? '',
			subclassificationId: interacaoSelected?.subclassificationId ?? '',
			description: interacaoSelected?.description ?? '',
			action: interacaoSelected?.action ?? '',
		})
		setSelectedCrId(interacaoSelected?.crId ?? '')
	}, [form, interacaoSelected])

	const selectedClassification = useMemo(() => {
		return classificationData?.items.find((c) => c.id === classificationId)
	}, [classificationData?.items, classificationId])

	const personOptions = useMemo(() => {
		const mapById = new Map<string, PersonEdit>()

		for (const person of persons) {
			mapById.set(person.id, person)
		}

		if (interacaoSelected?.leaderId && interacaoSelected?.leaderName) {
			mapById.set(interacaoSelected.leaderId, {
				id: interacaoSelected.leaderId,
				name: interacaoSelected.leaderName,
				badge: '',
			})
		}

		if (interacaoSelected?.employeeId && interacaoSelected?.employeeName) {
			mapById.set(interacaoSelected.employeeId, {
				id: interacaoSelected.employeeId,
				name: interacaoSelected.employeeName,
				badge: '',
			})
		}

		return Array.from(mapById.values()).map((person) => ({
			value: person.id,
			label: person.badge ? `${person.name} (${person.badge})` : person.name,
		}))
	}, [interacaoSelected, persons])

	const immediateActionOptions = [
		{
			value: 'CONGRATULATED',
			label: t('inspections.interaction.actionCongratulated'),
		},
		{ value: 'ORIENTED', label: t('inspections.interaction.actionOriented') },
	]

	const handleSave = (formData: InteracaoFormData) => {
		if (
			!formData.date ||
			!formData.leaderId ||
			!formData.employeeId ||
			!formData.classificationId ||
			!formData.subclassificationId ||
			!formData.description ||
			!formData.action ||
			!formData.potential
		) {
			toast.error(t('inspections.interaction.validationError'))
			return
		}

		// Envia apenas os campos que o usuario alterou (diff contra o form.reset inicial)
		const { dirtyFields } = form.formState
		const payload: InteracaoUpdateInput = {}

		if (selectedCrId && selectedCrId !== (interacaoSelected?.crId ?? '')) {
			payload.crId = selectedCrId
		}
		if (dirtyFields.classificationId) {
			payload.classificationId = formData.classificationId
		}
		if (dirtyFields.subclassificationId) {
			payload.subclassificationId = formData.subclassificationId
		}
		if (dirtyFields.date) {
			payload.interactionDate = formData.date
		}
		if (dirtyFields.leaderId) {
			payload.leaderId = formData.leaderId
		}
		if (dirtyFields.employeeId) {
			payload.employeeId = formData.employeeId
		}
		if (dirtyFields.description) {
			payload.description = formData.description
		}
		if (dirtyFields.action) {
			payload.action = formData.action as InteracaoAction
		}
		if (dirtyFields.potential) {
			payload.potential = formData.potential.toUpperCase()
		}

		if (Object.keys(payload).length === 0) {
			onClose()
			return
		}

		onSave(payload)
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSave, handleError)}
				className="flex flex-col gap-5 px-1"
			>
				{crList && crList.length > 0 && (
					<FormItem
						label={t('inspections.common.contract')}
						isRequired={false}
						error=""
					>
						<FieldContent fieldName="crId">
							<SearchableSelect
								id="interaction-edit-cr"
								nameField="crId"
								value={selectedCrId}
								onValueChange={setSelectedCrId}
								options={crList.map((cr) => ({ value: cr.id, label: cr.name }))}
								placeholder={t('inspections.common.contract')}
								searchPlaceholder={t('common.search')}
							/>
						</FieldContent>
					</FormItem>
				)}

				<FormField
					name="date"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspection.dateLabel')}
							error={form.formState.errors.date?.message}
						>
							<InspectionDateField
								value={
									field.value
										? parseStringToDateForDisplay(field.value)
										: undefined
								}
								onChange={(date) =>
									field.onChange(date ? formatDateYMD(date) : '')
								}
							/>
						</FormItem>
					)}
				/>

				<Separator className="bg-border/60" />

				<FormField
					name="leaderId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspection.leaderFilter')}
							error={form.formState.errors.leaderId?.message}
						>
							<FieldContent fieldName={field.name}>
								<SearchableSelect
									id="interaction-leader"
									nameField={field.name}
									ref={field.ref}
									value={field.value}
									onValueChange={field.onChange}
									options={personOptions}
									placeholder={t(
										'administration.correctInspection.leaderPlaceholder',
									)}
									searchPlaceholder={t('common.search')}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="employeeId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspectionForm.issuerLabel')}
							error={form.formState.errors.employeeId?.message}
						>
							<FieldContent fieldName={field.name}>
								<SearchableSelect
									id="interaction-employee"
									nameField={field.name}
									ref={field.ref}
									value={field.value}
									onValueChange={field.onChange}
									options={personOptions}
									placeholder={t(
										'administration.correctInspectionForm.issuerPlaceholder',
									)}
									searchPlaceholder={t('common.search')}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="classificationId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('inspections.interaction.classification')}
							error={form.formState.errors.classificationId?.message}
						>
							<FieldContent fieldName={field.name}>
								<NativeSelect
									id="interaction-classification"
									name={field.name}
									ref={field.ref}
									value={field.value}
									onChange={(e) => {
										field.onChange(e)
										form.setValue('subclassificationId', '', {
											shouldDirty: true,
											shouldValidate: true,
										})
									}}
								>
									<NativeSelectOption value="" disabled>
										{t('inspections.interaction.selectClassification')}
									</NativeSelectOption>
									{classificationData?.items.map((classification) => (
										<NativeSelectOption
											key={classification.id}
											value={classification.id}
										>
											{classification.name}
										</NativeSelectOption>
									))}
								</NativeSelect>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="subclassificationId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('inspections.interaction.subclassification')}
							error={form.formState.errors.subclassificationId?.message}
						>
							<Field>
								<FieldContent fieldName={field.name}>
									<NativeSelect
										id="interaction-subclassification"
										name={field.name}
										ref={field.ref}
										value={field.value}
										onChange={field.onChange}
										disabled={!selectedClassification}
									>
										<NativeSelectOption value="" disabled>
											{t('inspections.interaction.selectSubclassification')}
										</NativeSelectOption>
										{selectedClassification?.subclassifications.map((sub) => (
											<NativeSelectOption key={sub.id} value={sub.id}>
												{sub.name}
											</NativeSelectOption>
										))}
									</NativeSelect>
								</FieldContent>
							</Field>
						</FormItem>
					)}
				/>

				<Separator className="bg-border/60" />

				<FormField
					name="description"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('inspections.interaction.description')}
							error={form.formState.errors.description?.message}
						>
							<FieldContent fieldName={field.name}>
								<Textarea
									id="interaction-description"
									name={field.name}
									ref={field.ref}
									value={field.value}
									onChange={field.onChange}
									placeholder={t(
										'inspections.interaction.descriptionPlaceholder',
									)}
									rows={4}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<Separator className="bg-border/60" />

				<FormField
					name="action"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('inspections.interaction.immediateAction')}
							error={form.formState.errors.action?.message}
						>
							<FieldContent fieldName={field.name}>
								<InspectionOptionGroup
									value={field.value}
									onChange={field.onChange}
									options={immediateActionOptions}
									activeClass={colors.optionActive}
									columns={2}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<div className="flex gap-3 pt-1">
					{isLoading ? (
						<Button
							disabled={isLoading}
							type="button"
							className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
						>
							<Spinner />
							{t('administration.correctInspection.saving')}
						</Button>
					) : (
						<>
							<Button
								type="button"
								variant="cancel"
								onClick={onClose}
								className="flex-1"
							>
								{t('administration.correctInspection.cancelButton')}
							</Button>
							<Button
								disabled={isLoading}
								type="submit"
								className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
							>
								{t('administration.correctInspection.saveEdit')}
							</Button>
						</>
					)}
				</div>
			</form>
		</Form>
	)
}
